import React from 'react';
import moment from 'moment/min/moment-with-locales';
import {
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
} from '@mui/material';
import { useTranslation } from "react-i18next";
import { RouteStatusChip, MercosulPlateBadge } from './tableTheme';

export const calcEst = false;
export const showEst = true;
export const optionsRequired = false;

export const bookingHistoryColumns = (role, settings, t, isRTL) => [
    { 
      title: t('booking_ref') || 'Ref. Rota', 
      field: 'reference',
      render: rowData => (
        <span style={{ fontWeight: 700, fontFamily: 'monospace', color: '#1e293b' }}>
          {rowData.reference || (rowData.id ? rowData.id.substring(0, 8).toUpperCase() : '-')}
        </span>
      ),
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left', whiteSpace: 'nowrap' }
    },
    { 
      title: t('booking_date') || 'Data / Horário', 
      field: 'bookingDate', 
      render: rowData => (
        <div style={{ fontSize: '0.8125rem' }}>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>
            {rowData.bookingDate ? moment(rowData.bookingDate).format('DD/MM/YYYY') : '-'}
          </div>
          <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
            {rowData.bookingDate ? moment(rowData.bookingDate).format('HH:mm') : ''}
          </div>
        </div>
      ),
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('booking_status') || 'Status', 
      field: 'status', 
      render: rowData => <RouteStatusChip status={rowData.status} t={t} />,
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('vehicle_no') || 'Veículo / Placa', 
      field: 'vehicle_number',
      render: rowData => <MercosulPlateBadge plate={rowData.vehicle_number} />,
      cellStyle: { textAlign: 'center' }
    },
    { 
      title: t('car_type') || 'Tipo', 
      field: 'carType',
      render: rowData => (
        <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.8125rem' }}>
          {rowData.carType || '-'}
        </span>
      ),
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('assign_driver') || 'Condutor', 
      field: 'driver_name',
      render: rowData => (
        <span style={{ fontWeight: 600, color: '#0f172a' }}>
          {rowData.driver_name || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Não atribuído</span>}
        </span>
      ),
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('pickup_address') || 'Origem', 
      field: 'pickupAddress', 
      render: rowData => (
        <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8125rem' }} title={rowData.pickupAddress}>
          <span style={{ color: '#10b981', marginRight: 4 }}>●</span>
          {rowData.pickupAddress || '-'}
        </div>
      ),
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' } 
    },
    { 
      title: t('drop_address') || 'Destino', 
      field: 'dropAddress', 
      render: rowData => (
        <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8125rem' }} title={rowData.dropAddress}>
          <span style={{ color: '#ef4444', marginRight: 4 }}>●</span>
          {rowData.dropAddress || '-'}
        </div>
      ),
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' } 
    },
    { 
      title: t('distance') || 'Distância', 
      field: 'distance',
      render: rowData => rowData.distance ? `${rowData.distance} km` : '-',
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left', whiteSpace: 'nowrap' } 
    },
    { 
      title: t('total_time') || 'Tempo Total', 
      field: 'total_trip_time',
      render: rowData => rowData.total_trip_time || '-',
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left', whiteSpace: 'nowrap' } 
    },
    { 
      title: t('fleetadmins') || 'Frota / Gestor', 
      hidden: role === 'admin' ? false : true, 
      field: 'fleetadmin',
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('cancellation_reason') || 'Motivo Cancelamento', 
      field: 'reason',
      hidden: true,
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' } 
    }
];

export const BookingModalBody = (props) => {
    const { t, i18n  } = useTranslation();
    const isRTL = i18n.dir();
    const { classes, handleChange, auth, profileData } = props;
    return (
        <span>
            {auth.profile.usertype === 'customer' && !auth.profile.firstName ?
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                margin="normal"
                required = {auth.profile.firstName ? false : true }
                fullWidth
                id="firstName"
                label={t('firstname')}
                name="firstName"
                autoComplete="firstName"
                onChange={handleChange}
                value={profileData.firstName}
                autoFocus
                className={isRTL==='rtl'?classes.inputRtl:null}
                style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
              />
            </Grid>
            : null }
            {auth.profile.usertype === 'customer' && !auth.profile.lastName ?
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                margin="normal"
                required = {auth.profile.lastName ? false : true }
                fullWidth
                id="lastName"
                label={t('lastname')}
                name="lastName"
                autoComplete="lastName"
                onChange={handleChange}
                value={profileData.lastName}
                className={isRTL==='rtl'?classes.inputRtl:null}
                style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
              />
            </Grid>
            : null }
            {auth.profile.usertype === 'customer' && !auth.profile.email ?
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                margin="normal"
                required = {auth.profile.email ? false : true }
                fullWidth
                id="email"
                label={t('email')}
                name="email"
                autoComplete="email"
                onChange={handleChange}
                value={profileData.email}
                className={isRTL==='rtl'?classes.inputRtl:null}
                style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
              />
            </Grid>
            : null }
            <Typography component="h2" variant="h5" style={{marginTop:15, color:'#000'}}>
                { t('estimate_fare_text')}
            </Typography>
        </span>
    )
}

export const validateBookingObj = (t, bookingObject, instructionData) => {
    delete bookingObject.driverEstimates;
    return { bookingObject };
}

export const PanicSettings = (props) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const { classes, data, handleTextChange } = props;
    return (
        <span>
            <Typography component="h1" variant="h5" style={{ marginTop: '15px', textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                {t('panic_num')}
            </Typography>
            <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="panic"
                label={t('panic_num')}
                className={isRTL === "rtl" ? [classes.rootRtl_1, classes.right] : null}
                name="panic"
                autoComplete="panic"
                onChange={handleTextChange}
                value={data.panic}
            />
        </span>
    )
}

export const DispatchSettings = (props) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const { autoDispatch, onChange } = props;
    return (
        <FormControlLabel
            style={{ flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}
            control={
                <Switch
                    checked={autoDispatch}
                    onChange={onChange}
                    name="autoDispatch"
                    color="primary"
                />
            }
        label={t('auto_dispatch')}
      />
    )
}

export const BookingImageSettings = (props) => {
    return null;
}

export const carTypeColumns = (t, isRTL, onClick) =>  [
    { 
      title: t('name'), 
      field: 'name', 
      render: rowData => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 600 }}>{rowData.name}</span>
          {(rowData.is_default || rowData.id?.startsWith?.('00000000')) && (
            <span style={{ 
              backgroundColor: '#e0f2fe', 
              color: '#0369a1', 
              fontSize: '11px', 
              padding: '2px 8px', 
              borderRadius: '9999px',
              fontWeight: 600
            }}>
              Padrão
            </span>
          )}
        </div>
      ),
      cellStyle: isRTL === 'rtl' ? { paddingRight: 40, textAlign: 'right' } : { paddingLeft: 40 }, 
      headerStyle: isRTL === 'rtl' ? { paddingRight: 40 } : { paddingLeft: 40 }
    },
    { 
      title: t('image'),  
      field: 'image', 
      cellStyle: { textAlign: 'center' },
      initialEditValue: '/images/cartypes/carro.png',
      render: rowData => {
        const imgSrc = rowData.image || (
          rowData.name === 'Moto' ? '/images/cartypes/moto.png' :
          (rowData.name === 'Van/Onibus' || rowData.name === 'Van / Ônibus') ? '/images/cartypes/van_onibus.png' :
          '/images/cartypes/carro.png'
        );
        return (
          <button 
            type="button"
            onClick={() => { onClick(rowData); }} 
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <img alt={rowData.name || 'CarImage'} src={imgSrc} style={{ width: 44, height: 44, objectFit: 'contain' }} />
          </button>
        );
      }
    },
    { title: t('base_fare'), field: 'base_fare', type: 'numeric', cellStyle:{ textAlign: 'center'}, initialEditValue: 0 },
    { title: t('rate_per_unit_distance'), field: 'rate_per_unit_distance', type: 'numeric', cellStyle:{ textAlign: 'center'}, initialEditValue: 0},
    { title: t('rate_per_hour'), field: 'rate_per_hour', type: 'numeric', cellStyle:{ textAlign: 'center'}, initialEditValue: 0},
    { title: t('min_fare'), field: 'min_fare', type: 'numeric', cellStyle:{ textAlign: 'center'}, initialEditValue: 0},
    { title: t('convenience_fee'), field: 'convenience_fees', type: 'numeric', cellStyle:{ textAlign: 'center'}, initialEditValue: 0},
    {
      title: t('convenience_fee_type'),
      field: 'convenience_fee_type',
      lookup: { flat: t('flat'), percentage: t('percentage')},
      cellStyle:{ textAlign: 'center'}
    },
    { title: t('extra_info'), field: 'extra_info' , cellStyle:{ textAlign:isRTL ==='rtl'? 'right' : 'left'}},
    { title: t('position'), field: 'pos', type: 'numeric'}
];

export const acceptBid = (selectedBooking, selectedBidder) => {
    return null;
}

export const BidModal = (props) => {
    return null
}

export const  downloadCsv = (data, fileName) => {
    const finalFileName = fileName.endsWith(".csv") ? fileName : `${fileName}.csv`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    a.setAttribute("download", finalFileName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}